import endOfDay from 'date-fns/endOfDay'
import format from 'date-fns/format'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../api'
import { getStartDate } from '../utils/dateTime'

const getInerval = (period: string, isNFT?: boolean) => {
  if ((period === '24H' || period === '1D' || period === '7D') && isNFT) {
    return '10min'
  }
  if (isNFT) {
    return '1day'
  }
  if (period === '5Y') {
    return '1week'
  }
  if (period === '1D' || period === '24H') {
    return '5min'
  }
  if (period === '1W' || period === '7D' || period === '1M') {
    return '30min'
  }
  if (['14D'].includes(period)) {
    return '10min'
  }

  return '1day'
}

const getAssetEvents = async (
  asset: number | string,
  period: string,
  isNFT?: boolean,
) => {
  const endDate = format(endOfDay(new Date()), 'yyyy-MM-dd\'T\'HH:mm:ssXX')
  const startDate = getStartDate(period, '', isNFT)

  const { data, error } = await supabase
    .from('asset_time_series')
    .select('*')
    .eq('asset_id', asset)
    .eq('interval', getInerval(period, isNFT))
    .gte('datetime', startDate)
    .lte('datetime', endDate)
    .order('datetime')

  if (error) {
    throw new Error(error.message)
  }

  return data
}

const hasAvailableHours = (datetime: string) => !datetime.includes('T00')
&& !datetime.includes('T01')
&& !datetime.includes('T02')
&& !datetime.includes('T03')
&& !datetime.includes('T04')
&& !datetime.includes('T05')
&& !datetime.includes('T06')

const shouldShowPrevDay = (datetime: string) => !hasAvailableHours(datetime) || datetime.includes('T07')

const getLastDayEvent = async (asset: string | number, period: string) => {
  const { data: prevDayData, error: prevDayDataError } = await supabase
    .from('asset_time_series')
    .select('*')
    .eq('asset_id', asset)
    .eq('interval', getInerval(period))
    .lte('datetime', format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ssXX'))
    .order('datetime', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (prevDayDataError) {
    throw new Error(prevDayDataError.message)
  }

  return prevDayData
}

const getAssetEvents1Day = async (
  asset: number | string,
  period: string,
) => {
  const endDate = format(endOfDay(new Date()), 'yyyy-MM-dd\'T\'HH:mm:ssXX')
  const prevDayDate = await getLastDayEvent(asset, period)

  const { data, error } = await supabase
    .from('asset_time_series')
    .select('*')
    .eq('asset_id', asset)
    .eq('interval', getInerval(period))
    .gte('datetime', getStartDate(period, prevDayDate?.datetime))
    .lte('datetime', endDate)
    .order('datetime')
    .limit(288)

  if (error) {
    throw new Error(error.message)
  }

  const currentISODate = new Date(new Date().toISOString().slice(0, -1))
  const currentISODateString = format(currentISODate, 'yyyy-MM-dd\'T\'HH:mm:ssXX')

  const todayAssetEvents = data
    ?.filter((el) => !!(el.datetime.includes(currentISODateString.slice(0, 10))))

  const response = todayAssetEvents?.length
  && !shouldShowPrevDay(data[(data?.length ?? 0) - 1]?.datetime)
    ? todayAssetEvents
    : data

  if (response?.[0]?.datetime.includes('T19:55')) {
    response.shift()
  }

  return response.filter((el) => hasAvailableHours(el.datetime))
}

const getAssetEvents24H = async (
  asset: number | string,
  period: string,
  isNFT?: boolean,
) => {
  const endDate = format(endOfDay(new Date()), 'yyyy-MM-dd\'T\'HH:mm:ssXX')

  const { data, error } = await supabase
    .from('asset_time_series')
    .select('*')
    .eq('asset_id', asset)
    .eq('interval', getInerval(period, isNFT))
    .order('datetime', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message)
  }

  const { data: data2, error: error2 } = await supabase
    .from('asset_time_series')
    .select('*')
    .eq('asset_id', asset)
    .eq('interval', getInerval(period, isNFT))
    .gte('datetime', getStartDate(period, data?.datetime))
    .lte('datetime', endDate)
    .order('datetime')
    .limit(288)

  if (error2) {
    throw new Error(error2.message)
  }

  return data2
}

export default function useAssetEvents(
  asset: number | string,
  period: string,
  isNFT?: boolean,
) {
  return useQuery({
    queryKey: ['asset-events', asset, period, isNFT],
    queryFn: () => {
      if (['1D'].includes(period)) return getAssetEvents1Day(asset, period)
      if (['24H'].includes(period)) return getAssetEvents24H(asset, period, isNFT)
      return getAssetEvents(asset, period, isNFT)
    },
    enabled: Boolean(asset),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
  })
}
